// =============================================================================
// 404 NOT FOUND HANDLER COMPONENT
// =============================================================================

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface NotFoundHandlerProps {
  children: React.ReactNode;
}

// Define valid routes for the application
const VALID_ROUTES = [
  '/',
  '/about',
  '/services',
  '/portfolio',
  '/coaching',
  '/contact',
  '/payment',
  '/unsubscribe',
  '/terms',
  '/privacy',
  '/404'
];

// Valid dynamic routes patterns
const VALID_DYNAMIC_ROUTES = [
  /^\/portfolio\/[a-zA-Z0-9-]+$/,
  /^\/payment\/success$/
];

/**
 * Component that handles 404 detection and redirects to proper 404 page with status code
 */
export const NotFoundHandler: React.FC<NotFoundHandlerProps> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Check if the current path is a valid route
    const isValidRoute = VALID_ROUTES.includes(location.pathname) ||
                        VALID_DYNAMIC_ROUTES.some(pattern => pattern.test(location.pathname));

    // If we're not on a valid route, this is a 404
    if (!isValidRoute && location.pathname !== '/404') {
      // Set meta tags to indicate this is a 404 page
      const existingRobotsMeta = document.querySelector('meta[name="robots"]');
      if (existingRobotsMeta) {
        existingRobotsMeta.setAttribute('content', 'noindex,nofollow');
      } else {
        const robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        robotsMeta.content = 'noindex,nofollow';
        document.head.appendChild(robotsMeta);
      }

      // Add HTTP status meta tag
      const existingStatusMeta = document.querySelector('meta[name="http-status"]');
      if (existingStatusMeta) {
        existingStatusMeta.setAttribute('content', '404');
      } else {
        const statusMeta = document.createElement('meta');
        statusMeta.name = 'http-status';
        statusMeta.content = '404';
        document.head.appendChild(statusMeta);
      }

      // For direct URL access to non-existent routes, redirect to /404
      // This ensures the server returns a proper 404 status code
      if (typeof window !== 'undefined' && window.location.pathname !== '/404') {
        // Check if this is a direct navigation (not client-side routing)
        // We can detect this by checking if the referrer is from the same domain
        const isDirectNavigation = !document.referrer || 
                                  !document.referrer.includes(window.location.hostname);
        
        if (isDirectNavigation) {
          // Redirect to the 404.html file to get proper status code
          window.location.replace('/404.html');
          return;
        }
      }
    }
  }, [location.pathname]);

  return <>{children}</>;
};
